// Lib
import React, { useCallback } from 'react';
import { useDispatch } from 'react-redux';

// Utils
import { getElementId } from '../../../../../common/elements/utils/elementPropertyUtils';

import { keepEditorEditing } from './singleEditorSuggestionUtils';

// Actions
import { updateElement } from '../../../actions/elementActions';
import { addTaskListTitleAndStartEditing } from '../../taskListTitle/taskListTitleActions';

// Components
import FeatureSuggestion from '../../../suggestion/FeatureSuggestion';

// Constants
import { TITLE_SUGGESTION_ACK_PROPERTY } from '../../../../../common/taskLists/taskListConstants';

// Types
import type { ImMNElement } from '../../../../../common/elements/elementModelTypes';

// Answering the suggestion either way stops this list ever suggesting a title again
const TITLE_SUGGESTION_ACKNOWLEDGEMENT = { [TITLE_SUGGESTION_ACK_PROPERTY]: true };

type SingleEditorTitleSuggestionProps = {
    element: ImMNElement;
    question: React.ReactNode;
    isFocusedForegroundElement?: boolean;
    hideInstantly: () => void;
};

/**
 * Offers to add a title to a single-editor task list.
 *
 * Unlike the legacy suggestion there is no `showTitle` content property to set - the V2 title is a
 * node in the editor document - so confirming adds the node to the live editor and only the
 * acknowledgement is persisted.
 */
const SingleEditorTitleSuggestion: React.FC<SingleEditorTitleSuggestionProps> = (props) => {
    const { element, isFocusedForegroundElement, hideInstantly } = props;

    const dispatch = useDispatch();

    // We pass through "additionalChanges" so that the suggestion ack update & the title addition update
    // occur in a single ELEMENT_UPDATE action
    const onConfirm = useCallback(() => {
        hideInstantly();

        dispatch(
            addTaskListTitleAndStartEditing({
                element,
                isFocusedForegroundElement,
                additionalChanges: TITLE_SUGGESTION_ACKNOWLEDGEMENT,
            }),
        );
    }, [dispatch, element, hideInstantly, isFocusedForegroundElement]);

    const onDismiss = useCallback(() => {
        dispatch(updateElement({ id: getElementId(element), changes: TITLE_SUGGESTION_ACKNOWLEDGEMENT }));
    }, [dispatch, element]);

    return (
        <FeatureSuggestion {...props} onMouseDown={keepEditorEditing} onConfirm={onConfirm} onDismiss={onDismiss} />
    );
};

export default SingleEditorTitleSuggestion;
